import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { boolean, CompareObjects, DialogEvents, ErrorMessages, Field, FormBase, number, Optional, RecurrencePickerItem, RecurrenceType, Required, Scope, Validators } from '@sb/library/base';
import { DayOfWeekEnum, DaySelectionDisplayItem, LocalizationInfo, RepeatDetailsItem, RepeatFrequencyEnum, RepeatMonthTypeEnum } from '@sb/library/data-models';
import { LocalizationService, SBDate } from '@sb/library/localization';
import { filter, map, Observable, of, take } from 'rxjs';
import { FormFieldBaseScope } from '../../form-field-base.component';
import { FormFieldSubformBaseComponent } from '../../form-field-subform-base.component';
import { EditRecurrenceDialogComponent, EditRecurringOption } from './edit-recurrence-dialog/edit-recurrence-dialog.component';

export class FormFieldRecurrencePickerScope extends Scope {
    SetApplyToFutureMode = Field<(value: EditRecurringOption) => void>();
}

class RecurrencePickerForm extends FormBase {
    UseRecurrence = Required(RecurrenceType);
    Frequency = Required(RepeatFrequencyEnum);
    DaySelection = Required([DayOfWeekEnum]);
    Interval = Required(number);
    RepeatMonthType = Required(RepeatMonthTypeEnum);
    EndDate = Required(SBDate);
    RepeatIndefinitely = Optional(boolean);
}

@Component({
    selector: 'lib-input-recurrence-picker',
    templateUrl: './form-field-recurrence-picker.component.html',
    styleUrls: ['./form-field-recurrence-picker.component.scss']
})
export class FormFieldRecurrencePickerComponent extends FormFieldSubformBaseComponent<RecurrencePickerItem | null, RecurrencePickerForm> implements FormFieldRecurrencePickerScope {
    override Scope = this.SetScope({
        Implements: [FormFieldRecurrencePickerScope, FormFieldBaseScope]
    });

    @ViewChild('warningBanner') warningBanner!: ElementRef<HTMLElement>;

    readonly form = RecurrencePickerForm.Create(false);

    @Input()
    set startDate(value: SBDate | null) {
        if (value) {
            this.SetDateValues(value);
            this._showHoldExpiry = this.ShowHoldExpiry();
        }
    }

    @Input()
    set hasHoldOption(value: boolean | undefined) {
        this._hasHoldOption = value ?? false;
    };

    @Input() hideDaily: boolean = false;
    @Input() hideRepeatUntil: boolean = false;
    @Input() alwaysRepeat: boolean = false;

    private _localeInfo: LocalizationInfo = this._LocalizationService.LocalizationInfo;
    private _dayOfWeekEnums: DayOfWeekEnum[] = Object.values(DayOfWeekEnum);
    private _localizedAbbrDays: string[] = [];
    private _intervalSuffixText: string = '';
    private _translatedSelectedDays: string = '';
    private _dayOfMonth: number = 0;
    private _weekOfMonth: number = 0;
    private _dayOfWeek!: DayOfWeekEnum;
    private _minDate: SBDate = SBDate.Today();
    private _hasHoldOption: boolean = false;
    private _minHoldDate: SBDate = SBDate.Today();
    private _showHoldExpiry: boolean = false;

    private _initialRecurrence: RecurrencePickerItem = new RecurrencePickerItem();
    private _initialStartDate: SBDate | null = null;
    private _dateWarning: string = '';
    private _isEndDateAutoCorrected: boolean = false; //Start Date moved after End Date, So End Date is auto-corrected

    daysOfWeekErrors: ErrorMessages = {
        required: (value: any) => {
            return $localize`:@@LibraryFormFieldFrequencyPickerDaySelectionError:Please select at least one day of the week`
        }
    }
    intervalErrors: ErrorMessages = {
        max: (value: any) => {
            return $localize`:@@LibraryFormFieldFrequencyPickerIntervalSelectionError:Please enter a number less than or equal to 1000`
        },
        minExclusive: (value: any) => {
            return $localize`:@@LibraryFormFieldFrequencyPickerIntervalSelectionErrorMin:Please enter a number greater than 0`
        }
    }

    constructor(private _LocalizationService: LocalizationService) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.form.controls.Interval.AddValidators([Validators.minExclusive(0), Validators.max(1000)]);

        const dayNames: string[] = this._localeInfo.AbbreviatedDayNames;

        const index = dayNames.findIndex((_, i) => this._dayOfWeekEnums[i] == this._localeInfo.FirstDayOfWeek);
        this._localizedAbbrDays = dayNames.slice(index, dayNames.length).concat(dayNames.slice(0, index));
        this._dayOfWeekEnums = this._dayOfWeekEnums.slice(index, this._dayOfWeekEnums.length).concat(this._dayOfWeekEnums.slice(0, index));
        if (this._localeInfo.RightToLeft) {
            this._localizedAbbrDays.reverse();
            this._dayOfWeekEnums.reverse();
        }
        this.UpdateSuffixText();
        this.formControl.CustomPostValidationCheck = () => this.VerifyRecurrenceChanges();
    }

    writeFormValue(value: RecurrencePickerItem | null): void {
        value = this.InitializeOnWrite(value, RecurrencePickerItem);

        if (!this._hasHoldOption && value.IsHold) {
            console.error('Must set hasHoldOption to true to support IsHold in RecurrencePickerItem');
        }

        this._minHoldDate = SBDate.Today().AddDays(-value.HoldExpiry!);
        this._showHoldExpiry = this.ShowHoldExpiry();
       
        if (this._hasHoldOption && value.RepeatDetails) {
            this._hasHoldOption = false;
        }

        if (this.alwaysRepeat) {
            value.IsLastInSeries = true;   // Auto-invoicing doesn't warn about recurrence changes. If another use case arises, revisit this logic.
            if (!value.RepeatDetails) {
                value.RepeatDetails = new RepeatDetailsItem({
                    Frequency: RepeatFrequencyEnum.Weekly,
                    Interval: 1
                });
            }
        }
        
        this._initialRecurrence = new RecurrencePickerItem(JSON.parse(JSON.stringify(value)));

        this.SetDefaultValues(value);
    }

    TogglePicker(value: RecurrenceType): void {
        const data = this.formControl.value ?? new RecurrencePickerItem();
        switch (value) {
            case RecurrenceType.Once:
                this.UpdateFormControlRepeatDetails(null, false);
                data.IsHold = false;
                break;
            case RecurrenceType.HoldWeekly:
                this.UpdateFormControlRepeatDetails(null, false);
                data.IsHold = true;
                break;
            case RecurrenceType.Recurring:
                data.IsHold = false;
                this.PatchForm();
                break;
        }
        this._showHoldExpiry = this.ShowHoldExpiry();
        if (this._showHoldExpiry) {
            data.IsHold = false;
        }
        this.formControl.setValue(data, { emitModelToViewChange: false });
        this.SetDisabledStates();
    }

    SetDefaultValues(item: RecurrencePickerItem): void {
        const repeatDetails = item.RepeatDetails;

        this.form.patchValue({
            Frequency: repeatDetails?.Frequency ?? (this.hideDaily ? RepeatFrequencyEnum.Monthly : RepeatFrequencyEnum.Weekly),
            UseRecurrence: this.GetRecurrenceType(item),
            RepeatIndefinitely: repeatDetails ? repeatDetails.EndDate === null : true,
            DaySelection: repeatDetails?.DaySelection ? this.GetDayEnumsFromDaySelectionItem(repeatDetails?.DaySelection) : this._dayOfWeekEnums,
            Interval: repeatDetails?.Interval && repeatDetails.Interval !== 0 ? repeatDetails.Interval : 1,
            RepeatMonthType: repeatDetails?.MonthType ?? RepeatMonthTypeEnum.Absolute,
            EndDate: repeatDetails?.EndDate != null ? SBDate.FromISO(repeatDetails?.EndDate) : this._minDate,
        });

        this.SetDisabledStates();

        if (repeatDetails) {
            this.RepeatIndefinitelyChanged();
            this.UpdateSuffixText();
        }
    }

    GetDayEnumsFromDaySelectionItem(item: DaySelectionDisplayItem): DayOfWeekEnum[] {
        const dayOfWeekArray: DayOfWeekEnum[] = [];

        [item.Sunday, item.Monday, item.Tuesday, item.Wednesday, item.Thursday, item.Friday, item.Saturday].forEach((day, index) => {
            if (day) {
                dayOfWeekArray.push(Object.values(DayOfWeekEnum)[index]);
            }
        });

        return dayOfWeekArray;
    }

    PatchForm(): void {
        this.UpdateFormControlRepeatDetails({
            DaySelection: this.GetDaySelection(),
            EndDate: !this.form.controls.RepeatIndefinitely.value ? this.form.controls.EndDate.value!.ToISO() : null,
            Frequency: this.form.controls.Frequency.value,
            // if its daily we want to set the interval to 1 
            Interval: this.form.controls.Frequency.value == RepeatFrequencyEnum.Daily ? 1 : this.form.controls.Interval.value!,
            MonthType: this.form.controls.RepeatMonthType.value,
        }, false);
    }

    Changed(): void {
        this.PatchForm();
        this.GetTranslatedDays();
    }

    RepeatIndefinitelyChanged(): void {
        if (!this.form.controls.RepeatIndefinitely.value) {
            this.form.controls.EndDate.Enable();
        } else {
            this.form.controls.EndDate.Disable();
        }
        this.PatchForm();
    }

    FrequencyChanged(): void {
        this.SetDisabledStates();
        this.UpdateSuffixText();
        this.PatchForm();
    }

    SetApplyToFutureMode(value: EditRecurringOption): void {
        this.formControl.value!.ApplyToFutureEntries = value === EditRecurringOption.ThisAndFuture;
        this.AfterCheckValidityActions();
    }

    private GetRecurrenceType(item: RecurrencePickerItem): RecurrenceType {
        if (item.IsHold) {
            return RecurrenceType.HoldWeekly;
        } else if (!item.RepeatDetails) {
            return RecurrenceType.Once;
        } else {
            return RecurrenceType.Recurring;
        }
    }

    private GetDaySelection(): DaySelectionDisplayItem {
        let daySelectionItem = new DaySelectionDisplayItem();

        this.form.controls.DaySelection.value.forEach(day => {
            switch (day) {
                case DayOfWeekEnum.Sunday: daySelectionItem.Sunday = true; break;
                case DayOfWeekEnum.Monday: daySelectionItem.Monday = true; break;
                case DayOfWeekEnum.Tuesday: daySelectionItem.Tuesday = true; break;
                case DayOfWeekEnum.Wednesday: daySelectionItem.Wednesday = true; break;
                case DayOfWeekEnum.Thursday: daySelectionItem.Thursday = true; break;
                case DayOfWeekEnum.Friday: daySelectionItem.Friday = true; break;
                case DayOfWeekEnum.Saturday: daySelectionItem.Saturday = true; break;
            }
        });

        return daySelectionItem;
    }

    private GetTranslatedDays(): string {
        let translatedSelectedDays: string[] = [];
        this.form.controls.DaySelection.value.forEach((day: DayOfWeekEnum) => {
            const index = this._dayOfWeekEnums.indexOf(day);
            if (index >= 0) {
                translatedSelectedDays.push(this._localizedAbbrDays[index]);
            }
        });
        this._translatedSelectedDays = translatedSelectedDays.toString();

        return this._translatedSelectedDays;
    }

    private UpdateFormControlRepeatDetails(repeatDetails: RepeatDetailsItem | null, emitModelToViewChange?: boolean): void {
        if (this.formControl) {
            const formControlValue = this.formControl.value ?? new RecurrencePickerItem();
            formControlValue.RepeatDetails = repeatDetails;

            // Force update the form control so that value change subscriptions are triggered
            this.formControl.setValue(formControlValue, { emitModelToViewChange: emitModelToViewChange });
            this.UpdateDestructiveDateWarning();
        }
    }

    private UpdateDestructiveDateWarning() {
        const oldDetails = this._initialRecurrence.RepeatDetails;
        const newDetails = this.formControl.value!.RepeatDetails;

        const oldStart = oldDetails ? oldDetails.EndDate ? SBDate.FromISO(oldDetails.EndDate).totalMilliseconds : Infinity : 0;
        const newStart = newDetails ? newDetails.EndDate ? SBDate.FromISO(newDetails.EndDate).totalMilliseconds : Infinity : 0;

        if (this._initialRecurrence.IsLastInSeries || newStart >= oldStart) {
            this._dateWarning = '';
        } else if (this.formControl.value!.RepeatDetails?.EndDate) {
            this._dateWarning = SBDate.FromISO(this.formControl.value!.RepeatDetails.EndDate).Render('short');
        } else {
            this._dateWarning = this._minDate.Render('short');
        }

        if (this._dateWarning) {
            setTimeout(() => this.warningBanner?.nativeElement?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
        }
    }

    private VerifyRecurrenceChanges(): Observable<boolean> {
        const oldDetails = this._initialRecurrence.RepeatDetails;
        const newDetails = this.formControl.value!.RepeatDetails;

        if (oldDetails && oldDetails.EndDate) { // theres a bug where sometimes EndDate has the time component, so we ensure it has no time component
            oldDetails.EndDate = SBDate.FromISO(oldDetails.EndDate).ToISO();
        }

        if (newDetails && newDetails.EndDate) { // theres a bug where sometimes EndDate has the time component, so we ensure it has no time component
            newDetails.EndDate = SBDate.FromISO(newDetails.EndDate).ToISO();
        }

        if (!oldDetails || !newDetails || this._initialRecurrence.IsLastInSeries) {
            this.formControl.value!.ApplyToFutureEntries = true;
            this.AfterCheckValidityActions();
            return of(true);
        }

        const canOnlyUpdateSingle = (oldDetails.Frequency === RepeatFrequencyEnum.Daily && this._minDate.ToISO() !== this._initialStartDate!.ToISO()
                                    || oldDetails.Frequency === RepeatFrequencyEnum.Weekly && this._initialRecurrence.RepeatDetails!.EndDate && this._minDate.ToISO() > this._initialRecurrence.RepeatDetails!.EndDate);
        
        const dialogMode = !CompareObjects(oldDetails, newDetails) ? EditRecurringOption.ThisAndFuture
                         : canOnlyUpdateSingle ? EditRecurringOption.ThisOnly
                         : null;

        return this.CreateDialog(EditRecurrenceDialogComponent, dialogMode).events.pipe(
            filter(event => [DialogEvents.PrimaryAction, DialogEvents.Dismissed].includes(event)),
            map(event => event === DialogEvents.PrimaryAction),
            take(1)
        );
    }

    private AfterCheckValidityActions(): void {
        //Ensures that formControl.value.EndDate matches form.controls.EndDate
        if(this._isEndDateAutoCorrected 
            && !this.hideRepeatUntil 
            && this.form.controls.EndDate.enabled 
            && this.form.controls.UseRecurrence.value === RecurrenceType.Recurring){
            
            this.formControl.value!.RepeatDetails!.EndDate = !this.form.controls.RepeatIndefinitely.value ? this.form.controls.EndDate.value!.ToISO() : null;
            this._isEndDateAutoCorrected = false;
        }
    }

    private SetDateValues(dateToSet: SBDate): void {
        this._minDate = dateToSet;
        this._dayOfMonth = dateToSet.dayOfMonth;
        this._dayOfWeek = Object.values(DayOfWeekEnum)[dateToSet.dayOfWeek]; // we  can't use dayOfWeekEnums here because that has been localized to the user's locale and the calculation will be wrong
        this._weekOfMonth = Math.ceil(this._dayOfMonth / 7);
        if (this.form.controls.EndDate.value && this.form.controls.EndDate.value!.totalMilliseconds < dateToSet.totalMilliseconds) {
            this.form.controls.EndDate.setValue(dateToSet);
            this._isEndDateAutoCorrected = true;
        }
        if (!this._initialStartDate) {
            this._initialStartDate = dateToSet;
        }
    }

    private SetDisabledStates() {
        if (this.form.controls.UseRecurrence.value == RecurrenceType.Recurring) {
            if (this.form.controls.Frequency.value === RepeatFrequencyEnum.Daily) {
                this.form.controls.Interval.Disable();
                this.form.controls.RepeatMonthType.Disable();
                this.form.controls.DaySelection.Enable();
            } else if (this.form.controls.Frequency.value === RepeatFrequencyEnum.Weekly) {
                this.form.controls.Interval.Enable();
                this.form.controls.RepeatMonthType.Disable();
                this.form.controls.DaySelection.Disable();
            } else if (this.form.controls.Frequency.value === RepeatFrequencyEnum.Monthly) {
                this.form.controls.Interval.Enable();
                this.form.controls.RepeatMonthType.Enable();
                this.form.controls.DaySelection.Disable();
            } else { // Yearly
                this.form.controls.Interval.Enable();
                this.form.controls.RepeatMonthType.Disable();
                this.form.controls.DaySelection.Disable();
            }
        } else {
            this.form.controls.Interval.Disable();
            this.form.controls.RepeatMonthType.Disable();
            this.form.controls.DaySelection.Disable();
        }
    }

    private UpdateSuffixText() {
        if (this.form.controls.Frequency.value == RepeatFrequencyEnum.Weekly) {
            this._intervalSuffixText = $localize `:@@CommonWeeks:week(s)`;
        } else if (this.form.controls.Frequency.value == RepeatFrequencyEnum.Monthly) {
            this._intervalSuffixText = $localize `:@@CommonMonths:month(s)`;
        } else if (this.form.controls.Frequency.value == RepeatFrequencyEnum.Yearly) {
            this._intervalSuffixText = $localize `:@@CommonYears:year(s)`;
        }
    }

    private ShowHoldExpiry(): boolean {
        return this._hasHoldOption && this.form.controls.UseRecurrence.value === RecurrenceType.HoldWeekly 
                && this._minDate.ToISO() <= this._minHoldDate.ToISO();
    }

    get dayOfMonth(): number {
        return this._dayOfMonth;
    }

    get dayOfWeek(): DayOfWeekEnum {
        return this._dayOfWeek;
    }

    get minDate(): SBDate {
        return this._minDate;
    }

    get weekOfMonth(): number {
        return this._weekOfMonth;
    }

    get intervalSuffixText(): string {
        return this._intervalSuffixText;
    }

    get translatedSelectedDays(): string {
        return this._translatedSelectedDays;
    }

    get dayOfWeekEnums(): DayOfWeekEnum[] {
        return this._dayOfWeekEnums;
    }

    get localeInfo(): LocalizationInfo {
        return this._localeInfo;
    }

    get localizedAbbrDays(): string[] {
        return this._localizedAbbrDays;
    }

    get dateWarning(): string {
        return this._dateWarning;
    }

    get showHoldExpiry(): boolean {
        return this._showHoldExpiry;    
    }

    get hasHoldOption(): boolean {
        return this._hasHoldOption;
    }

    get RepeatFrequencyEnum(): typeof RepeatFrequencyEnum {
        return RepeatFrequencyEnum;
    }

    get RepeatMonthTypeEnum(): typeof RepeatMonthTypeEnum {
        return RepeatMonthTypeEnum;
    }

    get RecurrenceType(): typeof RecurrenceType {
        return RecurrenceType;
    }
}
